import React from 'react';
import {
    CheckCircle2,
    FileText,
    Info,
} from 'lucide-react';
import { closeBrowserTab } from '../utils/closeBrowserTab';

const SuccessSubmission = ({ offer, brand, contact, alreadySubmitted, onClose }) => {
    const lastDoc = offer.candidate?.documents?.[offer.candidate.documents.length - 1];

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'accepted': return 'text-green-600 bg-green-50';
            case 'rejected': return 'text-red-600 bg-red-50';
            case 'pending': return 'text-yellow-600 bg-yellow-50';
            default: return 'text-gray-600 bg-gray-50';
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4 font-inter">
            <div className="max-w-[420px] w-full bg-white rounded-2xl shadow-xl p-5 text-center border border-gray-100">
                <div className="mb-5">
                    {brand.logoUrl ? (
                        <img src={brand.logoUrl} alt={brand.name} className="h-10 mx-auto mb-3 object-contain" />
                    ) : (
                        <div className="text-lg font-bold text-gray-900 mb-3">{brand.name}</div>
                    )}

                    <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-3">
                        {alreadySubmitted ? (
                            <CheckCircle2 className="w-6 h-6 text-green-600" />
                        ) : (
                            <div className="text-2xl">🎉</div>
                        )}
                    </div>

                    <h1 className="text-xl font-bold text-gray-900 mb-1">
                        {alreadySubmitted ? 'Documents Submitted!' : 'Documents Uploaded Successfully!'}
                    </h1>
                    <p className="text-gray-500 text-xs leading-relaxed">
                        {alreadySubmitted
                            ? 'You have already successfully submitted your documents. No further action is required.'
                            : 'Thank you for uploading your documents. Your offer acceptance has been processed successfully.'}
                    </p>
                </div>

                <div className="bg-gray-50 rounded-xl p-3 mb-3 text-left border border-gray-100">
                    <h3 className="text-gray-700 font-bold text-xs mb-2 flex items-center uppercase tracking-wide">
                        <FileText className="w-3.5 h-3.5 mr-2 text-gray-400" />
                        Upload Summary
                    </h3>
                    <div className="space-y-1.5 text-xs">
                        <div className="flex justify-between border-b border-gray-100 pb-1">
                            <span className="text-gray-500">Documents Uploaded:</span>
                            <span className="text-gray-900 font-medium">{offer.candidate.documents?.length || 0} files</span>
                        </div>
                        {lastDoc?.uploadedAt && (
                            <div className="flex justify-between border-b border-gray-100 pb-1">
                                <span className="text-gray-500">Upload Time:</span>
                                <span className="text-gray-900 font-medium">
                                    {new Date(lastDoc.uploadedAt).toLocaleString()}
                                </span>
                            </div>
                        )}
                        <div className="flex justify-between border-b border-gray-100 pb-1">
                            <span className="text-gray-500">Offer Status:</span>
                            <span className={`font-bold px-2 py-0.5 rounded text-[10px] ${getStatusColor(offer.status)}`}>
                                {offer.status ? (offer.status.charAt(0).toUpperCase() + offer.status.slice(1)) : 'Unknown'}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-500">Candidate:</span>
                            <span className="text-gray-900 font-medium truncate ml-2">{offer.candidate.firstName} {offer.candidate.lastName}</span>
                        </div>
                    </div>
                </div>

                <div className="bg-green-50 rounded-xl p-3 mb-5 text-left border border-green-100">
                    <h4 className="text-green-800 font-bold text-xs mb-2 flex items-center">
                        <div className="w-4 h-4 mr-2 bg-green-200 rounded-full flex items-center justify-center text-[10px]">✨</div>
                        What Happens Next?
                    </h4>
                    <ul className="text-[11px] text-green-800 space-y-1 pl-1 font-medium">
                        <li>• Our HR team will review your uploaded documents</li>
                        <li>• You will receive a confirmation email within 24 hours</li>
                        <li>• Background verification process will be initiated</li>
                        <li>• You will be contacted for further onboarding steps</li>
                        <li>• Welcome to the team!</li>
                    </ul>
                </div>

                <div className="bg-amber-50 rounded-xl p-3 mb-5 text-left border border-amber-100">
                    <h4 className="text-amber-800 font-bold text-xs mb-2 flex items-center">
                        <div className="w-3.5 h-3.5 mr-2" >📞</div>
                        Need Help?
                    </h4>
                    <p className="text-[11px] text-amber-800 mb-1">
                        If you have any questions or need assistance, please contact our HR team:
                    </p>
                    {contact.email && (
                        <p className="text-[11px] text-amber-900 font-semibold">• Email: {contact.email}</p>
                    )}
                </div>

                <div className="flex flex-row justify-center gap-3">
                    {contact.email && (
                        <a
                            href={`mailto:${contact.email}`}
                            className="px-6 py-2 bg-blue-600 text-white font-bold rounded-full hover:bg-blue-700 transition-colors text-xs shadow-md shadow-blue-100 flex items-center"
                        >
                            Contact HR Team
                        </a>
                    )}
                    <button
                        onClick={onClose || closeBrowserTab}
                        className="px-6 py-2 bg-gray-600 text-white font-bold rounded-full hover:bg-gray-700 transition-colors text-xs shadow-md"
                    >
                        Close Window
                    </button>
                </div>

                <div className="mt-6 pt-4 border-t border-gray-50 text-[10px] text-gray-400">
                    <p className="font-bold text-gray-500 mb-1">{brand.name}</p>
                    Thank you for choosing to join our team!
                </div>
            </div>
        </div>
    );
};

export default SuccessSubmission;
